import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame, FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { changeKey, getChange, getPage, isUnknownPage, LandingPagesError, messageFor, pageKey, type PageDetailData } from "./api";
import { WindowControl, type Controls } from "./controls";
import { formatDay, funnelName, languageName, leadsLine, pageStatusLine, siblingLabel } from "./format";
import { PendingPanel, RecoverButton, ReplaceAssetButton, RetireButton } from "./WriteActions";

// UX spec S8 (one page: Copy · Assets · Leads). Spec: transparency
// specs/2026-09-24-landing-r5-page-detail.md; R6 (specs/2026-09-25-landing-r6-write-path.md) adds the
// pending-change panel, Replace on each image and Retire; a retired page offers Recover instead. Every figure is the Worker's, and the
// Worker sends counts only, never a person.

/** URL keys, beside the Funnels tab's `open` and `lang`. */
export const PAGE = "page";
export const SECTION = "section";

const SECTIONS = [
  { value: "", label: "Copy" },
  { value: "assets", label: "Assets" },
  { value: "leads", label: "Leads" },
];

const SECTION_SPEC: FilterSpec = { name: SECTION, label: "Section", kind: "choice", options: SECTIONS };

export function PageDetail({
  controls,
  folder,
  areaName,
  onBack,
}: {
  controls: Controls;
  folder: string;
  areaName: string;
  /** Back to the page's funnel; `null` when the page could not be read. */
  onBack: (funnelSlug: string | null) => void;
}) {
  const page = useQuery({
    queryKey: pageKey(folder, controls.days),
    queryFn: () => getPage(folder, controls.days),
    // A page the Worker cannot find will not appear on a retry; go straight to the draft check.
    retry: (failures, error) => !isUnknownPage(error) && failures < 1,
  });
  const section = typeof controls.values[SECTION] === "string" ? (controls.values[SECTION] as string) : "";

  // A failed background refetch keeps the last good page on screen: a new version is
  // briefly absent from the deployed site while its approval deploys (about two minutes).
  if (page.isError && !page.data) {
    return <UnreadablePage folder={folder} error={page.error} areaName={areaName} onBack={() => onBack(null)} />;
  }

  const data = page.data?.data;
  if (!data) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" onClick={() => onBack(data.page.funnel_slug)}>
            ← {funnelName(data.page.funnel_slug)}
          </Button>
          <h2 className="text-lg font-semibold text-ks-ink">{data.page.folder}</h2>
          {data.page.live && (
            <div className="ml-auto">
              <RetireButton
                folder={data.page.folder}
                pagePath={data.page.page_path}
                onlyLive={!data.siblings.some((s) => s.locale === data.page.locale && s.live)}
              />
            </div>
          )}
          {data.page.status === "retired" && !data.page.pending && (
            <div className="ml-auto">
              <RecoverButton folder={data.page.folder} />
            </div>
          )}
        </div>
        <p className="text-base text-ks-ink">{pageStatusLine(data.page)}</p>
        {data.siblings.length > 0 && (
          <nav aria-label="Other pages in this funnel" className="flex flex-wrap items-center gap-1 text-sm">
            <span className="text-muted-foreground">Also in:</span>
            {data.siblings.map((sibling) => (
              <Button
                key={sibling.folder}
                variant="link"
                size="sm"
                className="h-auto px-1"
                title={sibling.folder}
                onClick={() => controls.onChange({ ...controls.values, [PAGE]: sibling.folder })}
              >
                {siblingLabel(sibling)}
              </Button>
            ))}
          </nav>
        )}
      </header>

      <PendingPanel key={data.page.folder} folder={data.page.folder} />

      <FilterBar layout="segmented" filters={[SECTION_SPEC]} values={controls.values} onChange={controls.onChange} />

      {section === "assets" ? (
        <AssetsSection data={data} />
      ) : section === "leads" ? (
        <LeadsSection data={data} controls={controls} />
      ) : (
        <CopySection data={data} />
      )}
    </div>
  );
}

/**
 * The page could not be read. If it still has a change waiting for approval (a brand-new
 * page lives only on its console branch until approved), show that change so it can be
 * previewed and decided: the approve route does not depend on the page read, and a page
 * read fault must never lock a draft out of approval (bug 3fd0402f, keep-me / newsletter).
 */
export function UnreadablePage({
  folder,
  error,
  areaName,
  onBack,
}: {
  folder: string;
  error: unknown;
  areaName: string;
  onBack: () => void;
}) {
  // Any read fault can hide a draft except a permission or session one, which says nothing
  // about pending work; a change cached from earlier is ignored then too.
  const draftPossible = mayHideDraft(error);
  const change = useQuery({
    queryKey: changeKey(folder),
    queryFn: () => getChange(folder),
    enabled: draftPossible,
  });
  const pending = draftPossible ? (change.data?.data.pending ?? null) : null;
  let header: ReactNode;
  if (pending) {
    // The panel below carries the decision and, after Approve, the deploy state.
    header = (
      <Alert>
        <AlertTitle>{folder} · full view not available yet</AlertTitle>
        <AlertDescription>Copy, images and leads show here once the page is live.</AlertDescription>
      </Alert>
    );
  } else if (draftPossible && change.isPending) {
    header = (
      <Alert aria-busy="true">
        <AlertTitle>Checking for a change waiting for approval…</AlertTitle>
      </Alert>
    );
  } else {
    header = (
      <Alert>
        <AlertTitle>Page unavailable</AlertTitle>
        <AlertDescription>
          {messageFor(error, areaName)}
          {draftPossible && change.isError &&
            " Checking for a change waiting for approval also failed, so one may exist. Reload to try again."}
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <Button variant="ghost" className="self-start" onClick={onBack}>
        ← Pages
      </Button>
      {header}
      {draftPossible && <PendingPanel key={folder} folder={folder} />}
    </div>
  );
}

/** A 401/403 is about the reader, not the page; every other read fault may hide a draft. */
function mayHideDraft(error: unknown): boolean {
  return !(error instanceof LandingPagesError && (error.status === 401 || error.status === 403));
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 md:grid-cols-[10rem_1fr] md:gap-4">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-base text-ks-ink whitespace-pre-wrap break-words">{children}</dd>
    </div>
  );
}

function CopySection({ data }: { data: PageDetailData }) {
  const { page, copy } = data;
  return (
    <dl className="flex flex-col gap-3">
      <Field label="Template">{page.template_source ?? page.template}</Field>
      <Field label="Language">{languageName(page.locale)}</Field>
      <Field label="Version">{page.variant.toUpperCase()}</Field>
      <Field label="Search title">{copy.seo.title}</Field>
      <Field label="Search description">{copy.seo.description}</Field>
      <Field label="Kicker">{copy.hero.kicker}</Field>
      <Field label="Headline">{copy.hero.headline}</Field>
      <Field label="Sub">{copy.hero.sub}</Field>
      <Field label="Card">{`${copy.hero.card_heading}\n${copy.hero.card_body}`}</Field>
      <Field label="Button">{copy.hero.button}</Field>
      {copy.hero.proof !== null && <Field label="Proof">{copy.hero.proof}</Field>}
      <Field label={copy.points_heading}>
        {copy.points.map((point) => `${point.title}: ${point.body}`).join("\n")}
      </Field>
      <Field label={copy.how_heading}>{copy.steps.map((step, i) => `${i + 1}. ${step}`).join("\n")}</Field>
      <Field label="Bottom">{`${copy.bottom.heading}\n${copy.bottom.body}\nButton: ${copy.bottom.button}`}</Field>
      <Field label="After sign-up">{copy.success}</Field>
    </dl>
  );
}

function AssetsSection({ data }: { data: PageDetailData }) {
  return (
    <ul className="grid grid-cols-2 gap-4 md:grid-cols-3">
      {data.assets.map((asset) => (
        <li key={asset.slot} className="flex flex-col gap-2 rounded-lg border border-border p-3">
          <a href={asset.url} target="_blank" rel="noreferrer" className="block">
            <img
              src={asset.url}
              alt={asset.alt || `${asset.label} (decorative)`}
              loading="lazy"
              width={asset.width ?? undefined}
              height={asset.height ?? undefined}
              className="h-56 w-full rounded-md bg-muted object-contain"
            />
          </a>
          <p className="text-sm font-medium text-ks-ink break-all">{asset.file}</p>
          <p className="text-xs text-muted-foreground">{asset.label}</p>
          <p className="text-xs text-ks-ink">{asset.alt ? `Alt: ${asset.alt}` : "No alt text (decorative)"}</p>
          <div>
            <ReplaceAssetButton folder={data.page.folder} asset={asset} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function LeadsSection({ data, controls }: { data: PageDetailData; controls: Controls }) {
  const { leads, page } = data;
  const line = leadsLine(leads);
  return (
    <div className="flex flex-col gap-4">
      <WindowControl controls={controls} />
      {!leads.counts_available ? (
        <p className="text-sm text-muted-foreground">Lead counts unavailable. Copy and assets still work.</p>
      ) : leads.total === 0 && !page.live ? (
        <p className="text-sm text-muted-foreground">No leads until the page is live.</p>
      ) : (
        <>
          <p className="text-base text-ks-ink" aria-live="polite">
            {line}
          </p>
          <ChartFrame
            title={`Leads per day, last ${controls.days} days`}
            subtitle="Counts only. Each person is in Drip, tagged with funnel, product and language."
            titleVisible
            pointCount={leads.daily.length}
            emptyMessage="No leads in this window."
          >
            <BarChart data={leads.daily} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-ks-grey-4)" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12 }}
                stroke="var(--color-ks-grey-1)"
                tickFormatter={(day: string) => formatDay(day)}
              />
              <YAxis tick={{ fontSize: 12 }} stroke="var(--color-ks-grey-1)" allowDecimals={false} width={40} />
              <Tooltip
                labelFormatter={(day) => formatDay(String(day))}
                contentStyle={{
                  background: "var(--color-ks-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-ks-sm)",
                  fontSize: 12,
                }}
              />
              <Bar dataKey="leads" name="Leads" fill="var(--color-ks-purple)" isAnimationActive={false} />
            </BarChart>
          </ChartFrame>
        </>
      )}
    </div>
  );
}
